import React, { ChangeEvent, useEffect, useState } from 'react';

import { withSearch } from '@elastic/react-search-ui';
import { SearchContextState, SearchResult } from '@elastic/search-ui';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';

import { useSearchType } from 'components/SearchListing/search.context';

import InnerHtml from 'components/InnerHtml/InnerHtml';
import Link from 'components/Link';
import RawButton from 'components/RawButton/RawButton';

import { dataLayerPush } from 'utils/functions';

export const SearchInput = styled.input`
  height: 3.5rem;
  background-color: ${(props) => props.theme.colors.whiteSmoke};
  border: 1px solid ${(props) => props.theme.colors.borderLightGray2};
  border-radius: 3rem;
  margin-bottom: 0;
  padding-left: 1.5rem;
  width: 100%;
  font-size: 1.1rem;
  color: ${(props) => props.theme.colors.black};

  &::placeholder {
    font-size: 1.1rem;
    color: ${(props) => props.theme.colors.black};
    opacity: 1;
  }
`;

export const FaSearchIcon = styled(FontAwesomeIcon)`
  color: ${(props) => props.theme.colors.primaryRed};
  position: absolute;
  justify-items: center;
  padding: 0.75rem;
  font-family: ${(props) => props.theme.fonts.faSolid900};
  transition:
    background-color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1),
    color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  position: absolute;
  top: 0.5rem;
  bottom: 0;
  left: auto;
  right: 0.5rem;
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => props.theme.colors.primaryRed};
    color: ${(props) => props.theme.colors.white};
  }
`;

const SearchForm = styled.form`
  z-index: 11;
  position: relative;
  opacity: 1;
  grid-column-gap: 1.5rem;
  grid-row-gap: 1rem;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
  margin-bottom: 2rem;
`;

const SuggestionsContainer = styled.div`
  z-index: 100;
  position: absolute;
  width: 100%;
  top: 3.75rem;
  min-height: 1rem;
  box-shadow: 0 5px 11px ${({ theme }) => theme.colors.secondaryBoxShadowGray};
  border: 1px solid ${({ theme }) => theme.colors.borderLightGray2};
  background-color: ${({ theme }) => theme.colors.whiteSmoke};
`;

const SuggestionResultContainer = styled(Link)`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLightGray2};
  color: ${({ theme }) => theme.colors.primaryBlack2};
  text-decoration: none;

  grid-column-gap: 0.25rem;
  grid-row-gap: 0.25rem;
  padding: 1.5rem 2rem;
  transition: background-color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  display: flex;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondaryGray2};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const SuggestionTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
`;

const SuggestionBody = styled.div`
  font-size: 1rem;
  font-weight: 500;
`;

interface SearchBarProps {
  searchUrl: string;
  placeholder: string;
  suggestions?: boolean;
}

const getDedupedResults = (results: SearchResult[]) => {
  const seenUrl: { [key: string]: boolean } = {};

  return results.filter((result) => {
    const url = result.url_path.raw.replace(/\/$/, '');
    const seen = seenUrl[url];
    seenUrl[url] = true;

    return !seen;
  });
};

const SearchBar: React.FC<SearchBarProps & SearchContextState> = ({
  searchUrl,
  placeholder,
  suggestions,
  setSearchTerm,
  isLoading,
  results,
  searchTerm,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const { searchType } = useSearchType();

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;

    setSearchValue(newSearchValue);

    setSearchTerm(newSearchValue);
  };

  useEffect(() => {
    if (!isLoading && searchTerm && searchTerm.trim().length > 0 && results.length > 0) {
      dataLayerPush('view_search_results', 'event', {
        event_category: `${searchType} view search`,
        event_action: 'search result view',
        event_label: 'results',
        auto_or_serp: 'auto',
        search_term: searchValue,
      });
    }
  }, [results]);

  const handleResultClick = (title: string) => {
    dataLayerPush('select_search_result', 'event', {
      event_category: `${searchType} search`,
      event_action: 'search result click',
      event_label: 'results',
      suggested_result: title,
      auto_or_serp: 'auto',
      search_term: searchValue,
    });
  };

  return (
    <SearchForm action={searchUrl}>
      <SearchInput
        type={'text'}
        name={'q'}
        value={searchValue}
        onChange={handleChangeInput}
        placeholder={placeholder}
        autoComplete='off'
      />
      <RawButton aria-label='Search' type={'submit'}>
        <FaSearchIcon icon={faMagnifyingGlass} />
      </RawButton>
      {suggestions && !isLoading && searchTerm && searchTerm.trim().length > 0 && results.length > 0 && (
        <SuggestionsContainer>
          {getDedupedResults(results)
            .slice(0, 5)
            .map((result, index) => {
              const title = result.page_title?.raw ?? result.title?.raw ?? result.headings?.raw[0];
              const url = result.url_path?.raw;
              const copy = result.body_content
                ? title
                  ? result.body_content.raw
                      .trim()
                      .replace(new RegExp(`^${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} *`), '')
                  : result.body_content.raw.trim()
                : '';
              const lengthLimit = 250;
              const shortCopy =
                copy.length < lengthLimit
                  ? copy
                  : `${copy.slice(0, lengthLimit).trim().replaceAll(/[,.]$/g, '')}&hellip;`;

              return (
                <SuggestionResultContainer
                  to={url}
                  key={`${index}-${title}-${url}`}
                  onClick={() => handleResultClick(title)}
                >
                  <SuggestionTitle>{title}</SuggestionTitle>
                  <SuggestionBody>
                    <InnerHtml richText={shortCopy} />
                  </SuggestionBody>
                </SuggestionResultContainer>
              );
            })}
        </SuggestionsContainer>
      )}
    </SearchForm>
  );
};

export default withSearch<SearchBarProps, SearchContextState>((context) => context)(SearchBar);
